import React, { useState, useEffect, useCallback } from 'react';
import { UserCog, Plus, X, Mail, Users, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { usherAPI, UsherInvitation } from '../../services/usherAPI';
import { addNotification } from '../../store/slices/uiSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import ConfirmationModal from '../UI/ConfirmationModal';
import { AddUsherForm } from './AddUsherForm';
import { logError } from '../../utils/errorLogger';


interface UsherManagementProps {
  eventId: string;
  eventTitle: string;
  usherServiceEnabled?: boolean;
  onClose: () => void;
}

const UsherManagement: React.FC<UsherManagementProps> = ({
  eventId,
  eventTitle,
  usherServiceEnabled = false,
  onClose
}) => {
  const dispatch = useAppDispatch();
  const { theme } = useAppSelector((state) => state.ui);
  const [invitations, setInvitations] = useState<UsherInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddUsher, setShowAddUsher] = useState(false);
  const [showRevokedUshers, setShowRevokedUshers] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<{ isOpen: boolean; invitationId: string | null; usherName: string }>({
    isOpen: false,
    invitationId: null,
    usherName: ''
  });
  const [confirmUnrevoke, setConfirmUnrevoke] = useState<{ isOpen: boolean; invitationId: string | null; usherName: string }>({
    isOpen: false,
    invitationId: null,
    usherName: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchInvitations = useCallback(async (bypassCache: boolean = false) => {
    try {
      setLoading(true);
      const params = { event: eventId };
      const response = await usherAPI.getInvitations(params, bypassCache);
      
      // The API returns the data directly, not wrapped in a success object
      if (response && response.results) {
        setInvitations(response.results);
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        setInvitations(response);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      logError('Error fetching invitations', error);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    // Clear invitations when eventId changes to prevent showing wrong event's ushers
    setInvitations([]);
    fetchInvitations(true); // Always bypass cache when component mounts or eventId changes
  }, [eventId, fetchInvitations]);

  const handleUsherAdded = useCallback(async (responseData?: any) => {
    // Close the add usher form immediately
    setShowAddUsher(false);
    
    // The API returns { success: true, data: invitation_data }
    // usherAPI.createUsher returns response.data, which is the object with success, message, and data
    let newInvitation: UsherInvitation | null = null;
    if (responseData) {
      // Handle different response structures
      if (responseData.data) {
        // Backend returns { success: true, data: invitation_data }
        newInvitation = responseData.data as UsherInvitation;
      } else if (responseData.id) {
        // If the response itself is the invitation
        newInvitation = responseData as UsherInvitation;
      }
    }
    
    // If we have the invitation data, add it directly to the list (optimistic update)
    if (newInvitation) {
      setInvitations(prev => {
        // Check if invitation already exists to avoid duplicates
        const exists = prev.some(inv => inv.id === newInvitation!.id);
        if (exists) {
          // Update existing invitation instead of adding duplicate
          return prev.map(inv => inv.id === newInvitation!.id ? newInvitation! : inv);
        }
        // Add new invitation at the beginning
        return [newInvitation!, ...prev];
      });
    }
    
    // Always refresh the list to ensure we have the latest data from the server
    // Bypass cache to get fresh data
    const refreshList = async () => {
      try {
        await fetchInvitations(true); // Pass true to bypass cache
      } catch (error) {
        logError('Error refreshing invitations', error);
      }
    };
    
    // Refresh immediately with cache bypass
    await refreshList();
    
    // Also refresh after a short delay as a fallback
    setTimeout(refreshList, 500);
  }, [fetchInvitations]);

  // Separate active and revoked ushers
  const activeUshers = invitations.filter(invitation => invitation.status !== 'revoked');
  const revokedUshers = invitations.filter(invitation => invitation.status === 'revoked');

  // Component for rendering usher card
  const UsherCard = ({ invitation }: { invitation: UsherInvitation }) => {
    const isRevoked = invitation.status === 'revoked';
    
    return (
      <div className={`p-4 rounded-lg border space-y-3 ${
        isRevoked 
          ? theme === 'dark'
            ? 'bg-gray-800/50 border-gray-700 opacity-60 cursor-not-allowed'
            : 'bg-gray-100 border-gray-300 opacity-60 cursor-not-allowed'
          : theme === 'dark'
            ? 'bg-gray-800/30 border-gray-700'
            : 'bg-gray-50 border-gray-200'
      }`}>
        {/* Main usher info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isRevoked 
                ? theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                : theme === 'dark' ? 'bg-purple-900/50' : 'bg-purple-100'
            }`}>
              <UserCog className={`h-5 w-5 ${
                isRevoked 
                  ? theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  : theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
              }`} />
            </div>
            
            <div>
              <h4 className={`font-medium ${
                isRevoked 
                  ? theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  : theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {invitation.usher_name || `${invitation.usher.first_name} ${invitation.usher.last_name}`}
              </h4>
              <p className={`text-sm ${
                isRevoked 
                  ? theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {invitation.usher_phone || invitation.usher.phone}
              </p>
            </div>
          </div>
          
          {/* Show action buttons for active ushers, or unrevoke button for revoked ushers */}
          {isRevoked ? (
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 border-gray-600'
                  : 'bg-gray-200 text-gray-600 border-gray-300'
              }`}>
                Revoked
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const usherName = invitation.usher_name || `${invitation.usher.first_name} ${invitation.usher.last_name}`;
                  setConfirmUnrevoke({
                    isOpen: true,
                    invitationId: invitation.id,
                    usherName
                  });
                }}
                className="text-green-600 hover:text-green-700"
                title="Unrevoke usher"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const usherName = invitation.usher_name || `${invitation.usher.first_name} ${invitation.usher.last_name}`;
                  setConfirmRevoke({
                    isOpen: true,
                    invitationId: invitation.id,
                    usherName
                  });
                }}
                className="text-red-600 hover:text-red-700"
                title="Revoke usher"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        
        {/* Additional details */}
        <div className={`grid grid-cols-2 gap-4 text-sm border-t pt-3 ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-2">
            <Mail className={`h-4 w-4 ${
              isRevoked 
                ? theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`} />
            <span className={isRevoked 
              ? theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }>
              <span className="font-medium">Email:</span> {invitation.usher_email || invitation.usher?.email || 'Not provided'}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={isRevoked 
              ? theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
              : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }>📅</span>
            <span className={isRevoked 
              ? theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }>
              <span className="font-medium">Invited:</span> {new Date(invitation.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Users className={`h-4 w-4 ${
              isRevoked 
                ? theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`} />
            <span className={isRevoked 
              ? theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }>
              <span className="font-medium">Invited by:</span> {invitation.invited_by_name || 'Unknown'}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={isRevoked 
              ? theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
              : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }>⏰</span>
            <span className={isRevoked 
              ? theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }>
              <span className="font-medium">Expires:</span> {invitation.expires_at ? new Date(invitation.expires_at).toLocaleDateString() : 'Never'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      setIsProcessing(true);
      const response = await usherAPI.updateInvitation(invitationId, {
        status: 'revoked'
      });
      
      // The API should return the updated invitation data
      if (response) {
        dispatch(addNotification({
          type: 'error',
          title: 'Invitation Revoked',
          message: 'Invitation has been revoked',
        }));
        setConfirmRevoke({ isOpen: false, invitationId: null, usherName: '' });
        fetchInvitations(true);
      }
    } catch (error) {
      logError('Error revoking invitation', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to revoke invitation. Please try again.',
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnrevokeInvitation = async (invitationId: string) => {
    try {
      setIsProcessing(true);
      const response = await usherAPI.updateInvitation(invitationId, {
        status: 'pending'
      });
      
      // The API should return the updated invitation data
      if (response) {
        dispatch(addNotification({
          type: 'success',
          title: 'Invitation Restored',
          message: 'Invitation has been restored and is now active',
        }));
        setConfirmUnrevoke({ isOpen: false, invitationId: null, usherName: '' });
        fetchInvitations(true);
      }
    } catch (error) {
      logError('Error unrevoking invitation', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to restore invitation. Please try again.',
      }));
    } finally {
      setIsProcessing(false);
    }
  };



  // If usher service is not enabled, show message
  if (!usherServiceEnabled) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title={`Manage Ushers - ${eventTitle}`}
        size="md"
      >
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          <UserCog className={`h-12 w-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className="text-lg font-medium mb-2">Usher Service Not Enabled</p>
          <p className="text-sm">
            Usher service is not enabled for this event. Please enable it in event settings to manage ushers.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Manage Ushers - ${eventTitle}`}
      size="xl"
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {invitations.length} usher{invitations.length !== 1 ? 's' : ''} assigned
            </span>
          </div>
          
          <Button
            onClick={() => setShowAddUsher(true)}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Usher</span>
          </Button>
        </div>

        {/* Current Invitations */}
        <div className="space-y-4">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Assigned Ushers</h3>
          
          {loading ? (
            <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className={`w-6 h-6 border-2 rounded-full animate-spin mx-auto mb-4 ${
                theme === 'dark' ? 'border-gray-600 border-t-blue-500' : 'border-gray-300 border-t-blue-600'
              }`}></div>
              <p>Loading ushers...</p>
            </div>
          ) : invitations.length === 0 ? (
            <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              <UserCog className={`h-12 w-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
              <p>No ushers assigned to this event</p>
              <p className="text-sm">Click "Add Usher" to assign ushers for attendance tracking</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active Ushers */}
              <div className="space-y-3">
                <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Active Ushers ({activeUshers.length})</h4>
                {activeUshers.length === 0 ? (
                  <p className={`text-sm italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>No active ushers</p>
                ) : (
                  activeUshers.map((invitation) => (
                    <UsherCard key={invitation.id} invitation={invitation} />
                  ))
                )}
              </div>

              {/* Revoked Ushers Accordion */}
              {revokedUshers.length > 0 && (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowRevokedUshers(!showRevokedUshers)}
                    className={`flex items-center space-x-2 text-md font-medium transition-colors ${
                      theme === 'dark'
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    {showRevokedUshers ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span>Revoked Ushers ({revokedUshers.length})</span>
                  </button>
                  
                  {showRevokedUshers && (
                    <div className={`space-y-3 pl-4 border-l-2 ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      {revokedUshers.map((invitation) => (
                        <UsherCard key={invitation.id} invitation={invitation} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Usher Form */}
        <AddUsherForm
          eventId={eventId}
          isOpen={showAddUsher}
          onClose={() => setShowAddUsher(false)}
          onUsherAdded={handleUsherAdded}
        />
      </div>

      {/* Revoke Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmRevoke.isOpen}
        onClose={() => setConfirmRevoke({ isOpen: false, invitationId: null, usherName: '' })}
        onConfirm={() => {
          if (confirmRevoke.invitationId) {
            handleRevokeInvitation(confirmRevoke.invitationId);
          }
        }}
        title="Revoke Usher Invitation"
        message={`Are you sure you want to revoke the invitation for ${confirmRevoke.usherName}? This will prevent them from accessing the event.`}
        confirmText="Yes, Revoke"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={isProcessing}
      />

      {/* Unrevoke Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmUnrevoke.isOpen}
        onClose={() => setConfirmUnrevoke({ isOpen: false, invitationId: null, usherName: '' })}
        onConfirm={() => {
          if (confirmUnrevoke.invitationId) {
            handleUnrevokeInvitation(confirmUnrevoke.invitationId);
          }
        }}
        title="Restore Usher Invitation"
        message={`Are you sure you want to restore the invitation for ${confirmUnrevoke.usherName}? They will be able to access the event again.`}
        confirmText="Yes, Restore"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={isProcessing}
      />
    </Modal>
  );
};

export default UsherManagement;
